<div class="container py-4">
  <!-- Header Card with Draft Button AND Published Schedule Dropdown -->
  <div class="card bg-linear-gradient-purple text-white mb-4 p-2">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h2 class="mb-1">
          <i class="bi bi-calendar3 me-2"></i>Schedule Generator
        </h2>
        <p class="mb-0 opacity-75">
          Upload employee data and create an optimized weekly schedule
        </p>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <!-- Load Draft Button -->
        <button
          class="btn btn-light btn-sm"
          (click)="loadDraft()"
          [disabled]="loadingDraft || !draftExists"
        >
          <i class="bi bi-folder-open me-1"></i>
          {{ loadingDraft ? "Loading..." : "Load Draft" }}
        </button>

        <!-- Load Published Schedule Dropdown -->
        <div class="d-flex gap-2" *ngIf="publishedSchedules.length > 0">
          <select
            class="form-select form-select-sm bg-white"
            [(ngModel)]="selectedPublishedSchedule"
            style="width: 200px"
          >
            <option value="" disabled selected>Select Published Week</option>
            <option
              *ngFor="let schedule of publishedSchedules"
              [value]="schedule.id"
            >
              {{ schedule.label }}
            </option>
          </select>

          <button
            class="btn btn-light btn-sm"
            (click)="loadPublishedSchedule()"
            [disabled]="loadingPublished || !selectedPublishedSchedule"
          >
            <i class="bi bi-upload me-1"></i>
            {{ loadingPublished ? "Loading..." : "Load" }}
          </button>
        </div>
      </div>
    </div>
    <div class="mt-2 d-flex gap-3">
      <small *ngIf="lastSaved" class="opacity-75">
        <i class="bi bi-clock-history me-1"></i>
        Last saved: {{ lastSaved | date: "short" }}
      </small>
      <small *ngIf="lastPublished" class="opacity-75">
        <i class="bi bi-check-circle-fill me-1"></i>
        Last published: {{ lastPublished | date: "short" }}
      </small>
    </div>
  </div>

  <!-- Upload Section -->
  <div class="card shadow-sm mb-4">
    <div class="card-header bg-white border-bottom">
      <h5 class="mb-0 text-dark">
        <i class="bi bi-upload me-2 text-primary"></i>Step 1: Upload Employee
        Data
      </h5>
    </div>
    <div class="card-body">
      <!-- Download Button -->
      <div class="mb-3">
        <button
          class="btn btn-gradient w-100"
          (click)="downloadEmployeesJson()"
        >
          <i class="bi bi-download me-2"></i>
          Download Employee JSON
        </button>
      </div>

      <!-- Upload Input -->
      <div class="mb-3">
        <input
          type="file"
          (change)="onFileSelected($event)"
          class="form-control form-control-lg"
          accept=".json"
        />
      </div>

      <!-- Success Message -->
      <div
        *ngIf="employeeData"
        class="alert alert-success d-flex align-items-center"
        role="alert"
      >
        <i class="bi bi-check-circle-fill me-2 fs-5"></i>
        <div>
          <strong>File loaded successfully!</strong>
          <span class="ms-2">Ready to generate schedule</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Generate Section -->
  <div class="card shadow-sm mb-4">
    <div class="card-header bg-white border-bottom">
      <h5 class="mb-0 text-dark">
        <i class="bi bi-gear-fill me-2 text-primary"></i>Step 2: Generate
        Schedule
      </h5>
    </div>
    <div class="card-body text-center py-4">
      <div class="row justify-content-center mb-3">
        <div class="col-md-3">
          <label class="form-label fw-semibold">Week Start</label>
          <input type="date" class="form-control" [(ngModel)]="weekStart" />
        </div>
        <div class="col-md-3">
          <label class="form-label fw-semibold">Week End</label>
          <input type="date" class="form-control" [(ngModel)]="weekEnd" />
        </div>
      </div>
      <div class="mb-3">
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          (click)="setUpcomingWeek()"
        >
          <i class="bi bi-calendar-plus me-1"></i>
          Set Upcoming Week (Sun – Sat)
        </button>
      </div>
      <button
        class="btn btn-gradient btn-lg px-5"
        (click)="generate()"
        [disabled]="loading || !employeeData || !weekStart || !weekEnd"
      >
        <i class="bi bi-magic me-2"></i>
        {{ loading ? "Generating..." : "Generate Schedule" }}
      </button>
      <div class="mt-3">
        <small class="text-muted" *ngIf="!employeeData">
          <i class="bi bi-exclamation-circle me-1"></i>
          Please upload employee data first
        </small>
      </div>
      <div class="mt-3">
        <small class="text-muted" *ngIf="!weekEnd || !weekStart">
          <i class="bi bi-exclamation-circle me-1"></i>
          Please select a valid week start and end date
        </small>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="card shadow-sm mb-4 border-primary">
    <div class="card-body text-center py-5">
      <div
        class="spinner-border text-primary mb-3"
        role="status"
        style="width: 3rem; height: 3rem"
      >
        <span class="visually-hidden">Loading...</span>
      </div>
      <h5 class="text-primary mb-2">
        <i class="bi bi-cpu me-2"></i>Generating Your Schedule
      </h5>
      <p class="text-muted mb-3">
        Creating an optimized schedule based on employee availability and
        business rules...
      </p>
    </div>
  </div>

  <!-- Results Section -->
  <div *ngIf="result && result.length" class="card shadow-sm mb-4">
    <div class="card-header bg-success text-white">
      <div class="d-flex justify-content-between align-items-start mb-2">
        <div class="flex-grow-1">
          <div class="d-flex align-items-center gap-2 mb-1">
            <h5 class="mb-0 d-flex align-items-center">
              <i class="bi bi-check-circle-fill me-2"></i>
              Step 3: Schedule Actions
            </h5>
            <button
              class="btn btn-sm btn-outline-light"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#step3Info"
              aria-expanded="false"
              aria-controls="step3Info"
            >
              <i class="bi bi-info-circle"></i>
            </button>
          </div>

          <small class="opacity-75">Review and edit as needed</small>

          <!-- Collapsible info section -->
          <div class="collapse mt-2" id="step3Info">
            <div class="card card-body bg-success text-white border-0">
              <strong>Draft:</strong> Save your progress while building a
              schedule. Drafts are private and not visible to employees.<br /><br />

              <strong>Publish:</strong> Makes the schedule visible to employees
              for the selected week range. You can publish multiple schedules
              for different weeks.<br /><br />

              <strong>Editing:</strong> To make changes to an existing schedule,
              simply create and publish a new schedule using the same date range
              — it will overwrite the previous one for that week.<br /><br />

              <strong>Reminder:</strong> Upload the PDF version every Wednesday
              from the Management Dashboard.
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2 mt-2">
          <button
            class="btn action-btn"
            (click)="openPublishModal()"
            [disabled]="publishing"
            data-bs-toggle="tooltip"
            title="Publish this schedule so employees can view it"
          >
            <i class="bi bi-upload me-1"></i>
            Publish Schedule
          </button>

          <button
            class="btn action-btn"
            (click)="downloadPDF()"
            data-bs-toggle="tooltip"
            title="Download this schedule as a PDF copy"
          >
            <i class="bi bi-file-pdf me-1"></i>
            Download PDF
          </button>
        </div>
      </div>
    </div>

    <div class="card-body">
      <div class="alert alert-info mb-3" role="alert">
        <i class="bi bi-pencil-square me-2"></i>
        <strong>Tip:</strong> All fields are editable. Make any adjustments
        needed before saving or publishing.
      </div>

      <!-- Color Legend - Collapsible -->
      <div class="alert alert-light border mb-3" role="alert">
        <div class="d-flex justify-content-between align-items-center">
          <strong><i class="bi bi-palette me-2"></i>Color Guide</strong>
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#colorGuide"
            aria-expanded="false"
            aria-controls="colorGuide"
          >
            <i class="bi bi-chevron-down"></i>
          </button>
        </div>
        <div class="collapse" id="colorGuide">
          <div class="d-flex flex-wrap gap-2 mt-2" style="font-size: 0.85rem">
            <span>
              <span class="badge" style="color: #dc3545; font-weight: 700"
                >●</span
              >
              Name Color: 0 Shifts (Red)
            </span>
            <span>
              <span class="badge" style="color: #ca57de; font-weight: 700"
                >●</span
              >
              Name Color: Key Holder (Purple)
            </span>
            <span>
              <span class="badge" style="color: #d4a017; font-weight: 700"
                >●</span
              >
              Name Color: Full-Time + Cashier (Gold)
            </span>
            <span>
              <span class="badge" style="color: #28a745; font-weight: 700"
                >●</span
              >
              Name Color: Cashier Only (Green)
            </span>
            <span>
              <span class="badge" style="color: #0d6efd; font-weight: 700"
                >●</span
              >
              Name Color: Full-Time Only (Blue)
            </span>
            <span>
              <span class="badge" style="color: #000000; font-weight: 700"
                >●</span
              >
              Name Color: Default (Black)
            </span>
          </div>
        </div>
      </div>

      <!-- Employee Status Legend - Collapsible -->
      <div class="alert alert-light border mb-3" role="alert">
        <div class="d-flex justify-content-between align-items-center">
          <strong><i class="bi bi-people me-2"></i>Employee Status</strong>
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#employeeStatus"
            aria-expanded="false"
            aria-controls="employeeStatus"
          >
            <i class="bi bi-chevron-down"></i>
          </button>
        </div>
        <div class="collapse" id="employeeStatus">
          <div class="d-flex flex-wrap gap-2 mt-2" style="font-size: 0.85rem">
            <span>
              <span
                class="badge text-danger"
                style="font-weight: 600; font-size: 1em"
                >●</span
              >
              0 Shifts
            </span>
            <span>
              <span
                class="badge"
                style="font-weight: 600; font-size: 1em; color: #ca57de"
                >●</span
              >
              Key Holder
            </span>
            <span>
              <span
                class="badge"
                style="font-weight: 600; font-size: 1em; color: #d4a017"
                >●</span
              >
              Full-Time + Cashier (Gold)
            </span>
            <span>
              <span
                class="badge"
                style="font-weight: 600; font-size: 1em; color: #28a745"
                >●</span
              >
              Cashier Only
            </span>
            <span>
              <span
                class="badge"
                style="font-weight: 600; font-size: 1em; color: #0d6efd"
                >●</span
              >
              Full-Time Only
            </span>
            <span>
              <span
                class="badge"
                style="font-weight: 600; font-size: 1em; color: #000000"
                >●</span
              >
              Default
            </span>
          </div>
        </div>
      </div>

      <div
        class="table-responsive schedule-table-container"
        [class.maximized]="isResultsMaximized"
      >
        <table class="table table-hover table-bordered align-middle mb-0">
          <thead class="table-dark">
            <tr class="text-center">
              <th class="text-start">Name</th>
              <th>
                <div>Sun</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[0]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>
                <div>Mon</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[1]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>
                <div>Tue</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[2]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>
                <div>Wed</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[3]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>
                <div>Thu</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[4]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>
                <div>Fri</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[5]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>
                <div>Sat</div>
                <input
                  type="date"
                  class="form-control form-control-sm mt-1 bg-white text-dark"
                  [(ngModel)]="weekDates[6]"
                  style="font-size: 0.75rem; padding: 0.25rem"
                />
              </th>
              <th>Total Hours</th>
              <th>Days Off</th>
              <th>
                <div
                  class="d-flex align-items-center justify-content-center gap-2"
                >
                  <span>Actions</span>
                  <button
                    class="btn btn-sm btn-outline-light py-0 px-2"
                    type="button"
                    (click)="toggleResultsMaximize()"
                    [attr.aria-label]="
                      isResultsMaximized
                        ? 'Minimize schedule table'
                        : 'Maximize schedule table'
                    "
                    [title]="
                      isResultsMaximized
                        ? 'Exit full-screen table view'
                        : 'Expand table to full screen'
                    "
                  >
                    <i
                      class="bi"
                      [ngClass]="
                        isResultsMaximized
                          ? 'bi-fullscreen-exit'
                          : 'bi-arrows-fullscreen'
                      "
                    ></i>
                  </button>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr *ngFor="let employee of result; let i = index">
              <!-- Editable Name Field -->
              <td class="fw-bold text-nowrap align-middle">
                <div class="d-flex align-items-center">
                  <!-- <i class="bi bi-person-badge me-2 text-primary"></i> -->
                  <select
                    class="form-select form-select-sm"
                    [(ngModel)]="employee.name"
                    [ngClass]="getNameClass(employee)"
                    style="width: 7.5rem"
                    [ngStyle]="{
                      color: getNameColor(employee),
                      'font-weight': getNameFontWeight(employee),
                    }"
                    required
                  >
                    <option value="" disabled selected>Select employee</option>
                    <option *ngFor="let name of employeesList" [value]="name">
                      {{ name }}
                    </option>
                  </select>
                </div>
              </td>

              <!-- Day Fields with colored backgrounds -->
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Sun',
                    employee.Sun
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Sun"
                  placeholder="off"
                ></app-shift-time-input>
              </td>
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Mon',
                    employee.Mon
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Mon"
                  placeholder="off"
                ></app-shift-time-input>
              </td>
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Tue',
                    employee.Tue
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Tue"
                  placeholder="off"
                ></app-shift-time-input>
              </td>
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Wed',
                    employee.Wed
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Wed"
                  placeholder="off"
                ></app-shift-time-input>
              </td>
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Thu',
                    employee.Thu
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Thu"
                  placeholder="off"
                ></app-shift-time-input>
              </td>
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Fri',
                    employee.Fri
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Fri"
                  placeholder="off"
                ></app-shift-time-input>
              </td>
              <td
                [ngStyle]="{
                  'background-color': getCellBackgroundColor(
                    employee.name,
                    'Sat',
                    employee.Sat
                  ),
                }"
              >
                <app-shift-time-input
                  [(ngModel)]="employee.Sat"
                  placeholder="off"
                ></app-shift-time-input>
              </td>

              <!-- Hours & Days Off -->
              <td>
                <input
                  type="number"
                  class="form-control form-control-sm text-center fw-bold"
                  [(ngModel)]="employee.totalHours"
                  style="width: 70px"
                  step="0.5"
                />
              </td>
              <td>
                <input
                  type="number"
                  class="form-control form-control-sm text-center"
                  [(ngModel)]="employee.daysOff"
                  style="width: 60px"
                  min="0"
                  max="7"
                />
              </td>

              <!-- Delete Button -->
              <td class="text-center">
                <button
                  class="btn btn-sm btn-outline-danger"
                  (click)="openConfirmRemoveModal(i)"
                  title="Remove Row"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>

          <!-- Coverage Footer Row - Always at bottom of table -->
          <tfoot *ngIf="shiftsCount" class="table-light border-top-2">
            <tr class="text-center">
              <td class="fw-bold text-start align-middle">
                <i class="bi bi-clock me-2"></i>Shift Coverage
              </td>
              <!-- SUNDAY - Just total workers -->
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    (click)="openDayCoverageModal('Sun', 0)"
                    title="View scheduled employees for Sunday"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Sun"].totalWorkers }}
                  </button>
                </div>
              </td>
              <!-- WEEKDAYS - Openers, Mid, Closers, Total -->
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <div class="d-flex justify-content-center gap-1">
                    <span
                      class="badge bg-primary rounded-pill"
                      style="font-size: 0.7rem"
                      title="Openers (9-11 AM start)"
                    >
                      <i class="bi bi-sunrise" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Mon"].openers }}
                    </span>
                    <span
                      class="badge bg-info rounded-pill"
                      style="font-size: 0.7rem"
                      title="Mid Shift (11 AM - 7 PM)"
                    >
                      <i class="bi bi-clock" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Mon"].midShifts }}
                    </span>
                    <span
                      class="badge bg-warning text-dark rounded-pill"
                      style="font-size: 0.7rem"
                      title="Closers (end at 10 PM)"
                    >
                      <i class="bi bi-sunset" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Mon"].closers }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    title="View scheduled employees for Monday"
                    (click)="openDayCoverageModal('Mon', 1)"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Mon"].totalWorkers }}
                  </button>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <div class="d-flex justify-content-center gap-1">
                    <span
                      class="badge bg-primary rounded-pill"
                      style="font-size: 0.7rem"
                      title="Openers (9-11 AM start)"
                    >
                      <i class="bi bi-sunrise" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Tue"].openers }}
                    </span>
                    <span
                      class="badge bg-info rounded-pill"
                      style="font-size: 0.7rem"
                      title="Mid Shift (11 AM - 7 PM)"
                    >
                      <i class="bi bi-clock" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Tue"].midShifts }}
                    </span>
                    <span
                      class="badge bg-warning text-dark rounded-pill"
                      style="font-size: 0.7rem"
                      title="Closers (end at 10 PM)"
                    >
                      <i class="bi bi-sunset" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Tue"].closers }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    title="View scheduled employees for Tuesday"
                    (click)="openDayCoverageModal('Tue', 2)"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Tue"].totalWorkers }}
                  </button>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <div class="d-flex justify-content-center gap-1">
                    <span
                      class="badge bg-primary rounded-pill"
                      style="font-size: 0.7rem"
                      title="Openers (9-11 AM start)"
                    >
                      <i class="bi bi-sunrise" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Wed"].openers }}
                    </span>
                    <span
                      class="badge bg-info rounded-pill"
                      style="font-size: 0.7rem"
                      title="Mid Shift (11 AM - 7 PM)"
                    >
                      <i class="bi bi-clock" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Wed"].midShifts }}
                    </span>
                    <span
                      class="badge bg-warning text-dark rounded-pill"
                      style="font-size: 0.7rem"
                      title="Closers (end at 10 PM)"
                    >
                      <i class="bi bi-sunset" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Wed"].closers }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    title="View scheduled employees for Wednesday"
                    (click)="openDayCoverageModal('Wed', 3)"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Wed"].totalWorkers }}
                  </button>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <div class="d-flex justify-content-center gap-1">
                    <span
                      class="badge bg-primary rounded-pill"
                      style="font-size: 0.7rem"
                      title="Openers (9-11 AM start)"
                    >
                      <i class="bi bi-sunrise" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Thu"].openers }}
                    </span>
                    <span
                      class="badge bg-info rounded-pill"
                      style="font-size: 0.7rem"
                      title="Mid Shift (11 AM - 7 PM)"
                    >
                      <i class="bi bi-clock" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Thu"].midShifts }}
                    </span>
                    <span
                      class="badge bg-warning text-dark rounded-pill"
                      style="font-size: 0.7rem"
                      title="Closers (end at 10 PM)"
                    >
                      <i class="bi bi-sunset" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Thu"].closers }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    title="View scheduled employees for Thursday"
                    (click)="openDayCoverageModal('Thu', 4)"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Thu"].totalWorkers }}
                  </button>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <div class="d-flex justify-content-center gap-1">
                    <span
                      class="badge bg-primary rounded-pill"
                      style="font-size: 0.7rem"
                      title="Openers (9-11 AM start)"
                    >
                      <i class="bi bi-sunrise" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Fri"].openers }}
                    </span>
                    <span
                      class="badge bg-info rounded-pill"
                      style="font-size: 0.7rem"
                      title="Mid Shift (11 AM - 7 PM)"
                    >
                      <i class="bi bi-clock" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Fri"].midShifts }}
                    </span>
                    <span
                      class="badge bg-warning text-dark rounded-pill"
                      style="font-size: 0.7rem"
                      title="Closers (end at 10 PM)"
                    >
                      <i class="bi bi-sunset" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Fri"].closers }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    title="View scheduled employees for Friday"
                    (click)="openDayCoverageModal('Fri', 5)"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Fri"].totalWorkers }}
                  </button>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column justify-content-center gap-1">
                  <div class="d-flex justify-content-center gap-1">
                    <span
                      class="badge bg-primary rounded-pill"
                      style="font-size: 0.7rem"
                      title="Openers (9-11 AM start)"
                    >
                      <i class="bi bi-sunrise" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Sat"].openers }}
                    </span>
                    <span
                      class="badge bg-info rounded-pill"
                      style="font-size: 0.7rem"
                      title="Mid Shift (11 AM - 7 PM)"
                    >
                      <i class="bi bi-clock" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Sat"].midShifts }}
                    </span>
                    <span
                      class="badge bg-warning text-dark rounded-pill"
                      style="font-size: 0.7rem"
                      title="Closers (end at 10 PM)"
                    >
                      <i class="bi bi-sunset" style="font-size: 0.65rem"></i>
                      {{ shiftsCount["Sat"].closers }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="coverage-total-btn badge bg-success rounded-pill border-0"
                    style="font-size: 0.75rem"
                    title="View scheduled employees for Saturday"
                    (click)="openDayCoverageModal('Sat', 6)"
                  >
                    <i class="bi bi-people-fill" style="font-size: 0.7rem"></i>
                    {{ shiftsCount["Sat"].totalWorkers }}
                  </button>
                </div>
              </td>
              <td
                colspan="3"
                class="text-muted fst-italic"
                style="font-size: 0.85rem"
              >
                <i class="bi bi-info-circle me-1"></i>
                <span class="d-none d-lg-inline"
                  >Openers: 9-11 AM • Mid: 11 AM-7 PM • Closers: 10 PM</span
                >
              </td>
            </tr>
          </tfoot>
        </table>

        <!-- Action Buttons Below Table -->
        <div class="text-center mt-3">
          <button class="btn btn-primary btn-sm" (click)="addNewRow()">
            <i class="bi bi-plus-circle me-1"></i> Add New Row
          </button>
          <button class="btn btn-success btn-sm ms-2" (click)="recalculate()">
            <i class="bi bi-calculator me-1"></i> Recalculate
          </button>
          <button
            class="btn btn-warning btn-sm ms-2"
            (click)="openNotesOffcanvas()"
          >
            <i class="bi bi-sticky me-2"></i>View Notes
          </button>
          <button
            class="btn btn-dark btn-sm ms-2"
            (click)="saveDraft()"
            [disabled]="savingDraft"
            data-bs-toggle="tooltip"
            title="Save your progress as a private draft (not visible to employees)"
          >
            <i class="bi bi-floppy me-1"></i>
            {{ savingDraft ? "Saving..." : "Save Draft" }}
          </button>
        </div>

        <!-- OffCanvas For Notes -->
        <div
          class="offcanvas offcanvas-end"
          tabindex="-1"
          id="notesOffcanvas"
          aria-labelledby="notesOffcanvasLabel"
          style="width: 400px"
        >
          <div class="offcanvas-header bg-linear-gradient-purple text-white">
            <h5 class="offcanvas-title" id="notesOffcanvasLabel">
              <i class="bi bi-sticky me-2"></i>Employee Notes
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>

          <div class="offcanvas-body">
            <!-- Loading State -->
            <div *ngIf="loadingNotes" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted">Loading employee notes...</p>
            </div>

            <!-- No Employees State -->
            <div
              *ngIf="!loadingNotes && employeeNotes.length === 0"
              class="text-center py-5"
            >
              <i class="bi bi-inbox display-4 text-muted"></i>
              <p class="mt-3 text-muted">No employees found</p>
            </div>

            <!-- Employee Notes List -->
            <div *ngIf="!loadingNotes && employeeNotes.length > 0">
              <div
                class="alert bg-linear-gradient-purple text-white mb-3"
                role="alert"
              >
                <i class="bi bi-info-circle me-2"></i>
                <strong>{{ employeeNotes.length }}</strong> employee(s) found
              </div>

              <div class="accordion" id="notesAccordion">
                <div
                  class="accordion-item mb-2"
                  *ngFor="let employee of employeeNotes; let i = index"
                >
                  <h2 class="accordion-header" [id]="'heading' + i">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      [attr.data-bs-target]="'#collapse' + i"
                      aria-expanded="false"
                      [attr.aria-controls]="'collapse' + i"
                    >
                      <i class="bi bi-person-circle me-2"></i>
                      <strong>{{ employee.name }}</strong>
                      <span
                        *ngIf="!employee.notes"
                        class="badge bg-secondary ms-auto me-2"
                      >
                        No notes
                      </span>
                      <span
                        *ngIf="employee.notes"
                        class="badge bg-success ms-auto me-2"
                      >
                        Has notes
                      </span>
                    </button>
                  </h2>
                  <div
                    [id]="'collapse' + i"
                    class="accordion-collapse collapse"
                    [attr.aria-labelledby]="'heading' + i"
                    data-bs-parent="#notesAccordion"
                  >
                    <div class="accordion-body">
                      <!-- Availability Section -->
                      <div class="mb-3">
                        <h6 class="text-primary mb-2">
                          <i class="bi bi-calendar-week me-2"></i>Weekly
                          Availability
                        </h6>
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered mb-0">
                            <thead class="table-light">
                              <tr class="text-center">
                                <th style="font-size: 0.85rem">Sun</th>
                                <th style="font-size: 0.85rem">Mon</th>
                                <th style="font-size: 0.85rem">Tue</th>
                                <th style="font-size: 0.85rem">Wed</th>
                                <th style="font-size: 0.85rem">Thu</th>
                                <th style="font-size: 0.85rem">Fri</th>
                                <th style="font-size: 0.85rem">Sat</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="text-center" style="font-size: 0.8rem">
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Sunday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(employee, "Sunday")
                                  }}
                                </td>
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Monday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(employee, "Monday")
                                  }}
                                </td>
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Tuesday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(employee, "Tuesday")
                                  }}
                                </td>
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Wednesday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(
                                      employee,
                                      "Wednesday"
                                    )
                                  }}
                                </td>
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Thursday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(employee, "Thursday")
                                  }}
                                </td>
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Friday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(employee, "Friday")
                                  }}
                                </td>
                                <td
                                  [ngClass]="
                                    getAvailabilityClass(employee, 'Saturday')
                                  "
                                >
                                  {{
                                    getAvailabilityDisplay(employee, "Saturday")
                                  }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Notes Section -->
                      <div
                        *ngIf="employee.notes"
                        class="alert alert-light mb-0"
                      >
                        <strong><i class="bi bi-sticky me-2"></i>Notes:</strong>
                        <p class="mb-0 mt-2" style="white-space: pre-wrap">
                          {{ employee.notes }}
                        </p>
                      </div>
                      <div
                        *ngIf="!employee.notes"
                        class="text-muted fst-italic"
                      >
                        <i class="bi bi-dash-circle me-2"></i>
                        No notes available for this employee
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="border-top p-3 bg-light">
            <button
              class="btn btn-outline-secondary w-100"
              data-bs-dismiss="offcanvas"
            >
              <i class="bi bi-x-circle me-2"></i>Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="card-footer bg-light text-center">
    <small class="text-muted">
      <i class="bi bi-shield-check me-1"></i>
      Schedule generated on {{ getCurrentDate() }}
    </small>
  </div>
</div>

<!-- Custom Toast -->
<div class="custom-toast" *ngIf="toastVisible" [ngClass]="toastType">
  {{ toastMessage }}
</div>

<!-- Day Coverage Modal -->
<div
  class="modal fade"
  id="dayCoverageModal"
  tabindex="-1"
  aria-labelledby="dayCoverageModalLabel"
  aria-hidden="true"
>
  <div
    class="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered"
  >
    <div class="modal-content border-0 shadow-sm">
      <div class="modal-header bg-success text-white">
        <h5 class="modal-title" id="dayCoverageModalLabel">
          <i class="bi bi-people-fill me-2"></i>
          Scheduled Employees
          <span class="ms-2 fw-normal">{{ selectedCoverageDayHeader }}</span>
        </h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>
      <div class="modal-body">
        <div
          *ngIf="selectedCoverageEmployees.length === 0"
          class="text-center py-4 text-muted"
        >
          <i class="bi bi-calendar-x fs-3 d-block mb-2"></i>
          No scheduled employees for this day.
        </div>

        <div *ngIf="selectedCoverageEmployees.length > 0" class="row g-3">
          <div
            class="col-12 col-md-6"
            *ngFor="let item of selectedCoverageEmployees"
          >
            <div class="card h-100 coverage-employee-card">
              <div class="card-body">
                <h6
                  class="mb-2 fw-semibold"
                  [style.color]="getNameColor(item.entry)"
                >
                  {{ item.entry.name }}
                </h6>
                <p class="mb-2 text-dark">
                  <i class="bi bi-clock me-1"></i>
                  {{ item.shift }}
                </p>
                <div class="d-flex flex-wrap gap-1">
                  <span *ngIf="item.keyHolder" class="badge bg-secondary"
                    >Key Holder</span
                  >
                  <span *ngIf="item.fullTime" class="badge bg-primary"
                    >Full-Time</span
                  >
                  <span *ngIf="item.cashier" class="badge bg-success"
                    >Cashier</span
                  >
                  <span
                    *ngIf="!item.keyHolder && !item.fullTime && !item.cashier"
                    class="badge bg-dark"
                    >Part-Time</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Publish Confirmation Modal -->
<div
  class="modal fade"
  id="confirmPublishModal"
  tabindex="-1"
  aria-labelledby="confirmPublishLabel"
  aria-hidden="true"
>
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content border-0 shadow-sm">
      <div class="modal-header bg-primary text-white">
        <h5 class="modal-title" id="confirmPublishLabel">
          <i class="bi bi-upload me-2"></i>Publish Schedule
        </h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>
      <div class="modal-body">
        <p class="mb-3">
          You are about to <strong>publish this schedule</strong> for the
          selected week range.
        </p>

        <div
          class="alert alert-info mb-0"
          role="alert"
          *ngIf="publishWeekStart && publishWeekEnd"
        >
          <i class="bi bi-info-circle me-2"></i>
          <small>
            This will create a schedule with ID:
            <strong
              >{{ orgId }}-published-{{
                formatWeekRangeForId(publishWeekStart, publishWeekEnd)
              }}</strong
            ><br />
            If a schedule already exists for this week, it will be replaced with
            this new version.
          </small>
        </div>

        <small class="text-muted d-block mt-3">
          Employees will be able to view the updated version immediately once
          published.
        </small>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="confirmPublish()"
          [disabled]="publishing || !publishWeekStart || !publishWeekEnd"
        >
          <i class="bi bi-check-circle me-1"></i>
          {{ publishing ? "Publishing..." : "Yes, Publish" }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Confirm Remove Modal -->
<div
  class="modal fade"
  id="confirmRemoveModal"
  tabindex="-1"
  aria-labelledby="confirmRemoveLabel"
  aria-hidden="true"
>
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content border-0 shadow-sm">
      <div class="modal-header bg-danger text-white">
        <h5 class="modal-title" id="confirmRemoveLabel">
          <i class="bi bi-exclamation-triangle me-2"></i>Confirm Removal
        </h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>
      <div class="modal-body">
        <p class="mb-2">
          Are you sure you want to <strong>remove this employee row</strong>?
        </p>
        <small class="text-muted"> This action cannot be undone. </small>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
          Cancel
        </button>
        <button type="button" class="btn btn-danger" (click)="confirmRemove()">
          <i class="bi bi-trash me-1"></i> Yes, Remove
        </button>
      </div>
    </div>
  </div>
</div>
